/**
 * AdminDashboard — Painel de Controle unificado do assessor.
 * Sprint 10: Enhanced KPIs, navy palette, improved charts,
 * temporal comparison, segmentation with drill-down hints.
 *
 * Combina KPIs, ações pendentes, atividade recente e gráficos
 * num único dashboard operacional para o assessor solo.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { fmt, fmtK, Card, STitle, Badge } from "./adminUtils.jsx";
import { snapToData } from "../shared/dataUtils.js";
import { fetchLatestPtax } from "../supabaseClient.js";
import { fetchAuditLogs } from "../services/audit.js";
import { logWarn } from "../shared/logger.js";
import { RADIUS, TRANSITION } from "../shared/theme.js";
import SmartImportDropzone from "./components/SmartImportDropzone.jsx";
import { flexRow, flexCol, flexBetween } from "../shared/styles.js";

// ── Helpers ────────────────────────────────────────────────────
function parseReviewDate(dateStr) {
  if (!dateStr || dateStr === "–") return null;
  const iso = Date.parse(dateStr);
  if (!isNaN(iso)) return new Date(iso);
  const months = {
    janeiro: 0,
    fevereiro: 1,
    março: 2,
    abril: 3,
    maio: 4,
    junho: 5,
    julho: 6,
    agosto: 7,
    setembro: 8,
    outubro: 9,
    novembro: 10,
    dezembro: 11,
  };
  const m = dateStr.match(/(\d+)\s+de\s+(\w+)\s+de\s+(\d{4})/);
  if (m && months[m[2].toLowerCase()] !== undefined) {
    return new Date(+m[3], months[m[2].toLowerCase()], +m[1]);
  }
  return null;
}

const ACTION_COLORS = {
  snapshot_imported: { color: "#2563EB", bg: "#DBEAFE", label: "Import" },
  email_report_sent: { color: "#16A34A", bg: "#DCFCE7", label: "Email" },
  suitability_saved: { color: "#7C3AED", bg: "#EDE9FE", label: "Suitability" },
  tax_report_imported: { color: "#19376D", bg: "#DBEAFE", label: "IR" },
  login: { color: "#475569", bg: "#F1F5F9", label: "Login" },
  client_created: { color: "#0891B2", bg: "#CFFAFE", label: "Novo Cliente" },
  crm_event_created: { color: "#7C3AED", bg: "#EDE9FE", label: "CRM" },
  onboarding_request_sent: { color: "#D97706", bg: "#FEF3C7", label: "Onboarding" },
  onboarding_reminder_sent: { color: "#D97706", bg: "#FEF3C7", label: "Lembrete" },
};

function getActionMeta(action) {
  return ACTION_COLORS[action] || { color: "#475569", bg: "#F1F5F9", label: action };
}

function relativeTime(isoStr) {
  if (!isoStr) return "–";
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

// ── Hover-capable KPI wrapper ─────────────────────────────────
function HoverKPI({ children, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        transform: hovered ? "translateY(-2px)" : "none",
        boxShadow: hovered ? "0 4px 16px rgba(0,0,0,0.10)" : undefined,
        transition: TRANSITION.fast,
        cursor: onClick ? "pointer" : "default",
        borderRadius: RADIUS.md,
      }}
    >
      {children}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────
export default function AdminDashboard({
  dynamicClients,
  allClientsMeta,
  suitabilityDB,
  alertsSummary,
  onNavigate,
  onFileReady,
  T,
}) {
  const [ptaxBRL, setPtaxBRL] = useState(5.8);
  const [recentLogs, setRecentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => {
    fetchLatestPtax(5.8)
      .then(setPtaxBRL)
      .catch((err) => logWarn("AdminDashboard", "PTAX fetch failed", { error: err?.message }));
  }, []);

  const fetchLogs = useCallback(() => {
    fetchAuditLogs(15)
      .then((logs) => setRecentLogs(logs || []))
      .catch(() => setRecentLogs([]));
  }, []);

  useEffect(() => {
    setLogsLoading(true);
    fetchAuditLogs(15)
      .then((logs) => setRecentLogs(logs || []))
      .catch(() => setRecentLogs([]))
      .finally(() => setLogsLoading(false));
  }, []);

  // ── Auto-refresh audit logs every 30s ──
  const logsIntervalRef = useRef(null);
  useEffect(() => {
    logsIntervalRef.current = setInterval(fetchLogs, 30_000);
    return () => clearInterval(logsIntervalRef.current);
  }, [fetchLogs]);

  const allClients = dynamicClients;

  // ── KPIs ──────────────────────────────────────────────────
  const totalAUM = allClients.reduce((s, c) => s + (c.patrimonio?.total || 0), 0);
  const feeAnual = allClients.reduce((s, c) => {
    const aum = c.patrimonio?.total || 0;
    const fee = c.feeAnual ?? 1.0;
    return s + aum * (fee / 100);
  }, 0);
  const feeMediaPct = totalAUM > 0 ? +((feeAnual / totalAUM) * 100).toFixed(2) : 1.0;

  const clientsWithSnap = allClients
    .map((c) => {
      const sd = c._snap ? snapToData(c._snap) : null;
      return { aum: c.patrimonio?.total || 0, perf: sd?.perf, data: sd };
    })
    .filter((x) => x.perf && x.aum > 0);

  const totalAUMWithSnap = clientsWithSnap.reduce((s, x) => s + x.aum, 0);
  const weightedPerf = (() => {
    if (!clientsWithSnap.length || totalAUMWithSnap <= 0) {
      return { ret12m: 0, pctCDI12m: 0 };
    }
    const w = (field) =>
      clientsWithSnap.reduce((s, x) => s + (x.perf[field] || 0) * x.aum, 0) / totalAUMWithSnap;
    const ret12m = +w("ret12m").toFixed(2);
    const retCDI12m = +w("retCDI12m").toFixed(2);
    return {
      ret12m,
      pctCDI12m: retCDI12m > 0 ? +((ret12m / retCDI12m) * 100).toFixed(0) : 0,
    };
  })();

  // Onboarding KPI
  const onboardingCounts = useMemo(() => {
    const vals = Object.values(suitabilityDB || {});
    return {
      aprovado: vals.filter((s) => s.status === "aprovado").length,
      pendente: allClients.length - vals.filter((s) => s.status === "aprovado").length,
    };
  }, [allClients, suitabilityDB]);

  // ── Ações Pendentes ──────────────────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const overdueReviews = (allClientsMeta || []).filter((m) => {
    const d = parseReviewDate(m.next_review);
    return d && d < today;
  });

  const soonReviews = (allClientsMeta || []).filter((m) => {
    const d = parseReviewDate(m.next_review);
    if (!d || d < today) return false;
    return (d - today) / 86400000 <= 60;
  });

  const incompleteOnboarding = allClients.filter((c) => {
    const suit = suitabilityDB?.[c.id];
    return !suit || (suit.status !== "completo" && suit.status !== "aprovado");
  });

  const noReviewDate = (allClientsMeta || []).filter(
    (m) => !m.next_review || m.next_review === "–"
  );

  const pendingActions = [
    overdueReviews.length > 0 && {
      severity: "critical",
      icon: "🔴",
      label: `${overdueReviews.length} revisão(ões) vencida(s)`,
      detail: overdueReviews
        .slice(0, 3)
        .map((m) => m.client_id)
        .join(", "),
      nav: "Revisões",
    },
    incompleteOnboarding.length > 0 && {
      severity: "warning",
      icon: "🟡",
      label: `${incompleteOnboarding.length} onboarding(s) incompleto(s)`,
      detail: "CVM 30 pendente",
      nav: "Onboarding",
    },
    soonReviews.length > 0 && {
      severity: "info",
      icon: "🟠",
      label: `${soonReviews.length} revisão(ões) nos próximos 60 dias`,
      detail: "Agendar preparação",
      nav: "Revisões",
    },
    noReviewDate.length > 0 && {
      severity: "neutral",
      icon: "⚫",
      label: `${noReviewDate.length} cliente(s) sem data de revisão`,
      detail: "Definir próxima revisão",
      nav: "Clientes",
    },
  ].filter(Boolean);

  // ── Charts data ──────────────────────────────────────────
  const bestSnapClient = clientsWithSnap.reduce(
    (best, x) =>
      (x.data?.cumulative?.length || 0) > (best?.data?.cumulative?.length || 0) ? x : best,
    clientsWithSnap[0] || null
  );
  const refData = bestSnapClient?.data || null;
  const acumulado = (() => {
    if (!clientsWithSnap.length || totalAUMWithSnap <= 0) return 0;
    return (
      clientsWithSnap.reduce((s, x) => s + (x.perf.acumulado || 0) * x.aum, 0) / totalAUMWithSnap
    );
  })();
  const aumStart = acumulado > 0 ? +(totalAUM / (1 + acumulado / 100)).toFixed(2) : totalAUM;
  const aumHistory = (refData?.cumulative || []).map((d) => ({
    m: d.m,
    aum: +(aumStart * (1 + (d.p || 0) / 100)).toFixed(0),
    cdi: +(aumStart * (1 + (d.cdi || 0) / 100)).toFixed(0),
  }));

  const brokers = [
    {
      name: "XP",
      v: allClients.reduce((s, c) => s + (c.patrimonio?.xp || 0), 0),
      color: "#2563EB",
    },
    {
      name: "BTG",
      v: allClients.reduce((s, c) => s + (c.patrimonio?.btg || 0), 0),
      color: "#7C3AED",
    },
    {
      name: "Inter/Avenue",
      v: allClients.reduce(
        (s, c) =>
          s +
          (c.patrimonio?.inter || 0) +
          (c.patrimonio?.avenue_usd ? c.patrimonio.avenue_usd * ptaxBRL : 0),
        0
      ),
      color: "#19376D",
    },
  ];

  // ── Client segmentation ──────────────────────────────────
  const segments = [
    { label: "< 500k", min: 0, max: 500000, count: 0, total: 0, color: T.muted },
    { label: "500k–1M", min: 500000, max: 1000000, count: 0, total: 0, color: T.info },
    { label: "1–3M", min: 1000000, max: 3000000, count: 0, total: 0, color: "#7C3AED" },
    { label: "3M+", min: 3000000, max: Infinity, count: 0, total: 0, color: "#19376D" },
  ];
  allClients.forEach((c) => {
    const p = c.patrimonio?.total || 0;
    const seg = segments.find((s) => p >= s.min && p < s.max);
    if (seg) {
      seg.count++;
      seg.total += p;
    }
  });

  // ── Segmentation bar chart data ──
  const segBarData = segments.map((s) => ({
    name: s.label,
    clientes: s.count,
    aum: +(s.total / 1000).toFixed(0),
  }));

  // ── Render ──────────────────────────────────────────────
  const severityBorder = {
    critical: "#DC2626",
    warning: "#D97706",
    info: "#F59E0B",
    neutral: "#94A3B8",
  };

  return (
    <div>
      {/* ── KPI Row ─────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))",
          gap: 14,
          marginBottom: 20,
        }}
      >
        {[
          {
            label: "AUM Total",
            v: fmt(totalAUM),
            sub: "Patrimônio consolidado",
            color: T.text,
            icon: "💼",
            nav: "Clientes",
          },
          {
            label: "Clientes Ativos",
            v: allClients.length,
            sub: "Sob gestão LPA",
            color: T.info,
            icon: "👥",
            nav: "Clientes",
          },
          {
            label: "Performance 12M",
            v: `${(weightedPerf.ret12m || 0).toFixed(2).replace(".", ",")}%`,
            sub: `${(weightedPerf.pctCDI12m || 0).toFixed(0)}% do CDI`,
            color: T.success,
            icon: "📈",
            nav: "Métricas",
          },
          {
            label: "Fee Projetado",
            v: fmtK(feeAnual),
            sub: `${feeMediaPct.toFixed(1).replace(".", ",")}% a.a.`,
            color: "#19376D",
            icon: "💰",
            nav: "Cobranças",
          },
          {
            label: "Onboarding",
            v: `${onboardingCounts.aprovado}/${allClients.length}`,
            sub: `${onboardingCounts.pendente} pendente(s)`,
            color: onboardingCounts.pendente > 0 ? "#D97706" : "#16A34A",
            icon: "📋",
            nav: "Onboarding",
          },
          {
            label: "Alertas",
            v: alertsSummary?.critical ?? 0,
            sub: `${alertsSummary?.total ?? 0} total · ${alertsSummary?.warning ?? 0} atenção`,
            color: (alertsSummary?.critical ?? 0) > 0 ? "#DC2626" : "#16A34A",
            icon: "🔔",
            nav: "Alertas",
          },
        ].map((k) => (
          <HoverKPI key={k.label} onClick={() => onNavigate && onNavigate(k.nav)}>
            <Card T={T} style={{ padding: "16px 20px" }}>
              <div style={{ ...flexBetween, marginBottom: 4 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: T.sub,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                  }}
                >
                  {k.label}
                </div>
                <span style={{ fontSize: 18 }}>{k.icon}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.v}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{k.sub}</div>
            </Card>
          </HoverKPI>
        ))}
      </div>

      {/* ── Quick Import Dropzone ─────────────────────────── */}
      {onFileReady && <SmartImportDropzone onFileReady={onFileReady} T={T} />}

      {/* ── Ações Pendentes ────────────────────────────────── */}
      {pendingActions.length > 0 && (
        <Card T={T} style={{ padding: "18px 22px", marginBottom: 18 }}>
          <STitle title="Ações Pendentes" sub="Itens que requerem sua atenção" T={T} />
          <div style={{ ...flexCol, gap: 10 }}>
            {pendingActions.map((a, i) => (
              <button
                key={i}
                onClick={() => onNavigate && onNavigate(a.nav)}
                style={{
                  ...flexRow,
                  gap: 12,
                  padding: "12px 16px",
                  background: T.bg,
                  border: "none",
                  borderLeft: `4px solid ${severityBorder[a.severity]}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  width: "100%",
                  transition: TRANSITION.fast,
                }}
              >
                <span style={{ fontSize: 18 }}>{a.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{a.label}</div>
                  <div style={{ fontSize: 11, color: T.muted }}>{a.detail}</div>
                </div>
                <span style={{ fontSize: 11, color: T.muted }}>Ir para {a.nav} →</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* ── Charts row ──────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr", gap: 18, marginBottom: 18 }}>
        <Card T={T}>
          <STitle title="Evolução do AUM" sub="Patrimônio total × CDI" T={T} />
          {aumHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={aumHistory} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="g_dash_aum" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#19376D" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#19376D" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g_dash_cdi" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis dataKey="m" tick={{ fontSize: 10, fill: T.sub }} />
                <YAxis
                  tickFormatter={(v) => fmtK(v)}
                  tick={{ fontSize: 10, fill: T.sub }}
                  width={70}
                />
                <Tooltip formatter={(v) => fmt(v)} labelStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="aum"
                  stroke="#19376D"
                  fill="url(#g_dash_aum)"
                  strokeWidth={2.5}
                  name="AUM LPA"
                />
                <Area
                  type="monotone"
                  dataKey="cdi"
                  stroke="#2563EB"
                  fill="url(#g_dash_cdi)"
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  name="CDI"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: 200,
                ...flexRow,
                justifyContent: "center",
                color: T.muted,
                fontSize: 13,
              }}
            >
              Importe snapshots para ver a evolução do AUM
            </div>
          )}
        </Card>

        <Card T={T}>
          <STitle title="AUM por Corretora" T={T} />
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={brokers}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={75}
                dataKey="v"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {brokers.map((b) => (
                  <Cell key={b.name} fill={b.color} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ ...flexRow, gap: 12, justifyContent: "center", marginTop: 4 }}>
            {brokers.map((b) => (
              <div key={b.name} style={{ ...flexRow, gap: 4, fontSize: 11, color: T.sub }}>
                <div
                  style={{ width: 8, height: 8, borderRadius: RADIUS.sm, background: b.color }}
                />
                {b.name}: {fmtK(b.v)}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Segmentação + Atividade Recente ──────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        {/* Segmentação */}
        <Card T={T}>
          <STitle title="Segmentação por Patrimônio" T={T} />
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}
          >
            {segments.map((seg) => (
              <div
                key={seg.label}
                style={{
                  padding: "14px 16px",
                  background: T.bg,
                  borderRadius: 8,
                  borderLeft: `3px solid ${seg.color}`,
                  cursor: "pointer",
                  transition: TRANSITION.fast,
                }}
                onClick={() => onNavigate && onNavigate("Clientes")}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 4 }}>
                  {seg.label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: seg.color }}>{seg.count}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{fmtK(seg.total)}</div>
              </div>
            ))}
          </div>
          {/* Mini bar chart */}
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={segBarData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: T.sub }} />
              <Tooltip
                formatter={(v, name) => [
                  name === "clientes" ? v : `R$ ${v}k`,
                  name === "clientes" ? "Clientes" : "AUM",
                ]}
              />
              <Bar dataKey="clientes" fill="#19376D" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Atividade Recente */}
        <Card T={T}>
          <STitle title="Atividade Recente" sub="Últimas ações no sistema" T={T} />
          {logsLoading ? (
            <div style={{ color: T.muted, fontSize: 12, padding: "20px 0" }}>Carregando...</div>
          ) : recentLogs.length === 0 ? (
            <div style={{ color: T.muted, fontSize: 12, padding: "20px 0" }}>
              Nenhuma atividade registrada
            </div>
          ) : (
            <div style={{ ...flexCol, gap: 6, maxHeight: 340, overflowY: "auto" }}>
              {recentLogs.slice(0, 12).map((log, i) => {
                const meta = getActionMeta(log.action);
                const ts = relativeTime(log.created_at);
                return (
                  <div
                    key={log.id || i}
                    style={{
                      ...flexRow,
                      gap: 10,
                      padding: "8px 10px",
                      background: i % 2 === 0 ? T.row1 : T.row2,
                      borderRadius: 6,
                    }}
                  >
                    <Badge text={meta.label} color={meta.color} bg={meta.bg} />
                    <div
                      style={{
                        flex: 1,
                        fontSize: 12,
                        color: T.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {log.action?.replace(/_/g, " ")}
                    </div>
                    <span style={{ fontSize: 10, color: T.muted, whiteSpace: "nowrap" }}>{ts}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ── Ações Rápidas ──────────────────────────────────── */}
      <Card T={T} style={{ padding: "18px 22px" }}>
        <STitle title="Ações Rápidas" T={T} />
        <div style={{ ...flexRow, gap: 10, flexWrap: "wrap" }}>
          {[
            { label: "Importar Extrato", nav: "Importar Dados", icon: "📥" },
            { label: "Novo Cliente", nav: "Clientes", icon: "➕" },
            { label: "Enviar Relatórios", nav: "Relatórios", icon: "📬" },
            { label: "Rebalanceamento", nav: "Rebalanceamento", icon: "⚖️" },
            { label: "Ver Métricas", nav: "Métricas", icon: "📊" },
            { label: "Cobranças", nav: "Cobranças", icon: "💳" },
            { label: "CRM", nav: "CRM", icon: "🤝" },
          ].map((a) => (
            <button
              key={a.nav}
              onClick={() => onNavigate && onNavigate(a.nav)}
              style={{
                ...flexRow,
                gap: 8,
                padding: "10px 18px",
                borderRadius: 8,
                border: `1px solid ${T.border}`,
                background: T.card,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                color: T.text,
                transition: TRANSITION.fast,
              }}
            >
              <span>{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

AdminDashboard.propTypes = {
  dynamicClients: PropTypes.array.isRequired,
  allClientsMeta: PropTypes.array,
  suitabilityDB: PropTypes.object,
  alertsSummary: PropTypes.object,
  onNavigate: PropTypes.func,
  onFileReady: PropTypes.func,
  T: PropTypes.object.isRequired,
};
